// Centralized Canadian financial data layer.
// EVERY value here is dated + sourced. Confirm/update before launch each tax year.
// Values updated to the 2026 tax year from official government sources (see per-block `source`).
// `verify: false` = confirmed against canada.ca / Service Canada. `verify: true` = confirm before launch.

export const DATA_YEAR = 2026;

export const VERIFY_NOTE =
  "Confirm the exact current-year figure with the official source before relying on it.";

// Federal tax brackets (2026) — SOURCE: Canada Revenue Agency, canada.ca/en/revenue-agency/services/tax/individuals/tax-rates-brackets
export const FEDERAL_TAX_BRACKETS = {
  year: 2026,
  source: "Canada Revenue Agency (CRA) — canada.ca",
  verify: false,
  brackets: [
    { upTo: 58523, rate: 0.14 },
    { upTo: 117045, rate: 0.205 },
    { upTo: 181440, rate: 0.26 },
    { upTo: 258482, rate: 0.29 },
    { upTo: Infinity, rate: 0.33 },
  ],
};

// Provincial/territorial tax brackets (2026) — SOURCE: CRA current-year tables + provincial finance/CRA T4032 payroll tables (confirmed).
export const PROVINCIAL_TAX_BRACKETS = {
  year: 2026,
  source: "CRA & provincial/territorial finance authorities (2026 confirmed)",
  verify: false,
  provinces: {
    AB: { name: "Alberta", brackets: [
      { upTo: 61200, rate: 0.08 }, { upTo: 154259, rate: 0.10 }, { upTo: 185111, rate: 0.12 },
      { upTo: 246813, rate: 0.13 }, { upTo: 370220, rate: 0.14 }, { upTo: Infinity, rate: 0.15 } ] },
    BC: { name: "British Columbia", brackets: [
      { upTo: 50363, rate: 0.0506 }, { upTo: 100728, rate: 0.077 }, { upTo: 115648, rate: 0.105 },
      { upTo: 140430, rate: 0.1229 }, { upTo: 190405, rate: 0.147 }, { upTo: 265545, rate: 0.168 },
      { upTo: Infinity, rate: 0.205 } ] },
    MB: { name: "Manitoba", brackets: [
      { upTo: 47000, rate: 0.108 }, { upTo: 100000, rate: 0.1275 }, { upTo: Infinity, rate: 0.174 } ] },
    NB: { name: "New Brunswick", brackets: [
      { upTo: 52333, rate: 0.094 }, { upTo: 104666, rate: 0.14 }, { upTo: 193861, rate: 0.16 },
      { upTo: Infinity, rate: 0.195 } ] },
    NL: { name: "Newfoundland and Labrador", brackets: [
      { upTo: 44678, rate: 0.087 }, { upTo: 89354, rate: 0.145 }, { upTo: 159528, rate: 0.158 },
      { upTo: 223340, rate: 0.178 }, { upTo: 285319, rate: 0.198 }, { upTo: 570638, rate: 0.208 },
      { upTo: 1141275, rate: 0.213 }, { upTo: Infinity, rate: 0.218 } ] },
    NS: { name: "Nova Scotia", brackets: [
      { upTo: 30995, rate: 0.0879 }, { upTo: 61991, rate: 0.1495 }, { upTo: 97417, rate: 0.1667 },
      { upTo: 157124, rate: 0.175 }, { upTo: Infinity, rate: 0.21 } ] },
    ON: { name: "Ontario", brackets: [
      { upTo: 53891, rate: 0.0505 }, { upTo: 107785, rate: 0.0915 }, { upTo: 150000, rate: 0.1116 },
      { upTo: 220000, rate: 0.1216 }, { upTo: Infinity, rate: 0.1316 } ] },
    PE: { name: "Prince Edward Island", brackets: [
      { upTo: 33928, rate: 0.095 }, { upTo: 65820, rate: 0.1347 }, { upTo: 106890, rate: 0.166 },
      { upTo: 142520, rate: 0.1762 }, { upTo: 200000, rate: 0.19 }, { upTo: Infinity, rate: 0.20 } ] },
    QC: { name: "Quebec", brackets: [
      { upTo: 54345, rate: 0.14 }, { upTo: 108680, rate: 0.19 }, { upTo: 132245, rate: 0.24 },
      { upTo: Infinity, rate: 0.2575 } ] },
    SK: { name: "Saskatchewan", brackets: [
      { upTo: 54532, rate: 0.105 }, { upTo: 155805, rate: 0.125 }, { upTo: Infinity, rate: 0.145 } ] },
    NT: { name: "Northwest Territories", brackets: [
      { upTo: 53003, rate: 0.059 }, { upTo: 106009, rate: 0.086 }, { upTo: 172346, rate: 0.122 },
      { upTo: Infinity, rate: 0.1405 } ] },
    NU: { name: "Nunavut", brackets: [
      { upTo: 55801, rate: 0.04 }, { upTo: 111602, rate: 0.07 }, { upTo: 181439, rate: 0.09 },
      { upTo: Infinity, rate: 0.115 } ] },
    YT: { name: "Yukon", brackets: [
      { upTo: 58523, rate: 0.064 }, { upTo: 117045, rate: 0.09 }, { upTo: 181440, rate: 0.109 },
      { upTo: 500000, rate: 0.128 }, { upTo: Infinity, rate: 0.15 } ] },
  },
};

export const PROVINCE_LIST = Object.entries(PROVINCIAL_TAX_BRACKETS.provinces).map(
  ([code, v]) => ({ code, name: v.name })
);

// Registered account limits (2026) — SOURCE: CRA, canada.ca (MP/RRSP/DPSP/TFSA limits table)
export const ACCOUNT_LIMITS = {
  year: 2026,
  source: "Canada Revenue Agency (CRA) — canada.ca",
  verify: false,
  tfsa: { annual: 7000, note: "2026 annual TFSA dollar limit. Total room depends on your history since eligibility (since 2009 if 18+ and resident)." },
  rrsp: { rate: 0.18, maxDollar: 33810, note: "18% of prior-year (2025) earned income up to the 2026 dollar maximum of $33,810." },
  fhsa: { annual: 8000, lifetime: 40000, note: "$8,000/year, $40,000 lifetime. Unused room (max $8,000) carries forward once the account is opened." },
  resp: {
    lifetimeContribution: 50000,
    cesgRate: 0.20,
    cesgAnnualMax: 500,
    cesgLifetimeMax: 7200,
    note: "Canada Education Savings Grant (CESG): 20% match up to $500/year, $7,200 lifetime per child.",
  },
};

// Government retirement benefits (2026) — SOURCE: Service Canada, canada.ca
export const GOV_BENEFITS = {
  year: 2026,
  source: "Service Canada — canada.ca",
  verify: false,
  cpp: { maxMonthlyAt65: 1507.65, avgMonthly: 900.00, note: "Maximum monthly CPP retirement pension at age 65 (2026). Average new pension is roughly $900/month — most people receive less than the maximum." },
  oas: { maxMonthly65to74: 742.31, maxMonthly75plus: 816.54, note: "Maximum monthly OAS for Jan–Mar 2026 (adjusted quarterly). Amount depends on years of residence in Canada after age 18." },
};

// Mortgage rules — SOURCE: OSFI / CMHC / FCAC
export const MORTGAGE_RULES = {
  year: 2026,
  source: "OSFI / CMHC / FCAC",
  verify: true,
  compounding: "semi-annual", // Canadian fixed mortgages compound semi-annually, not in advance
  gdsMax: 0.39, // Gross Debt Service ratio guideline
  tdsMax: 0.44, // Total Debt Service ratio guideline
  stressTestBuffer: 0.02, // qualify at greater of contract+2% or 5.25% benchmark
  stressTestFloor: 0.0525,
  minDownPayment: [
    { upTo: 500000, rate: 0.05 },
    { upTo: 1500000, rate: 0.10, note: "5% on first $500k, 10% on the portion above (insured up to $1.5M)" },
    { upTo: Infinity, rate: 0.20, note: "20% required at/above $1.5M (no CMHC insurance)" },
  ],
};

export const DATA_SOURCES = [
  { label: "Registered account limits (TFSA/RRSP/FHSA/RESP)", source: "Canada Revenue Agency (CRA)", year: 2026 },
  { label: "Federal tax brackets", source: "CRA (canada.ca)", year: 2026 },
  { label: "Provincial & territorial tax brackets", source: "CRA & provincial finance authorities", year: 2026 },
  { label: "CPP & OAS benefit amounts", source: "Service Canada", year: 2026 },
  { label: "Mortgage qualification rules & stress test", source: "OSFI / CMHC / FCAC", year: 2026 },
];


// Payroll deductions (2026) — SOURCE: CRA
export const PAYROLL = {
  year: 2026,
  cpp: { rate: 0.0595, ympe: 74600, yampe: 85000, basicExemption: 3500, cpp2Rate: 0.04 },
  ei: { rate: 0.0163, mie: 68900 },
};

// Market-rate assumptions (manually editable defaults) — review regularly.
export const MARKET_RATES = {
  updated: "2026-01",
  prime: 5.95,
  mortgage5yrFixed: 4.79,
  heloc: 6.70,
  gic5yr: 4.10,
};

// Sales tax by province/territory (2026). total = combined rate applied to price.
export const SALES_TAX = {
  year: 2026,
  provinces: {
    AB: { name: "Alberta", gst: 5, pst: 0, total: 5, type: "GST" },
    BC: { name: "British Columbia", gst: 5, pst: 7, total: 12, type: "GST+PST" },
    MB: { name: "Manitoba", gst: 5, pst: 7, total: 12, type: "GST+PST" },
    NB: { name: "New Brunswick", gst: 0, pst: 0, total: 15, type: "HST" },
    NL: { name: "Newfoundland and Labrador", gst: 0, pst: 0, total: 15, type: "HST" },
    NS: { name: "Nova Scotia", gst: 0, pst: 0, total: 14, type: "HST" },
    ON: { name: "Ontario", gst: 0, pst: 0, total: 13, type: "HST" },
    PE: { name: "Prince Edward Island", gst: 0, pst: 0, total: 15, type: "HST" },
    QC: { name: "Quebec", gst: 5, pst: 9.975, total: 14.975, type: "GST+QST" },
    SK: { name: "Saskatchewan", gst: 5, pst: 6, total: 11, type: "GST+PST" },
    NT: { name: "Northwest Territories", gst: 5, pst: 0, total: 5, type: "GST" },
    NU: { name: "Nunavut", gst: 5, pst: 0, total: 5, type: "GST" },
    YT: { name: "Yukon", gst: 5, pst: 0, total: 5, type: "GST" },
  },
};

// RRIF minimum withdrawal factors (post-2015 rules). Under 71 uses 1/(90-age).
export const RRIF_FACTORS = {
  71: 0.0528, 72: 0.0540, 73: 0.0553, 74: 0.0567, 75: 0.0582, 76: 0.0598, 77: 0.0617,
  78: 0.0636, 79: 0.0658, 80: 0.0682, 81: 0.0708, 82: 0.0738, 83: 0.0771, 84: 0.0808,
  85: 0.0851, 86: 0.0899, 87: 0.0955, 88: 0.1021, 89: 0.1099, 90: 0.1192, 91: 0.1306,
  92: 0.1449, 93: 0.1634, 94: 0.1879, 95: 0.2000,
};
export function rrifFactor(age) {
  if (age >= 95) return RRIF_FACTORS[95];
  if (age >= 71) return RRIF_FACTORS[age] || RRIF_FACTORS[71];
  return 1 / (90 - age);
}

// Land Transfer Tax rules — SOURCE: provincial statutes (ON, BC precise; others estimated).
export const LAND_TRANSFER = {
  year: 2026,
  ON: {
    name: "Ontario",
    brackets: [
      { upTo: 55000, rate: 0.005 }, { upTo: 250000, rate: 0.01 },
      { upTo: 400000, rate: 0.015 }, { upTo: 2000000, rate: 0.02 }, { upTo: Infinity, rate: 0.025 },
    ],
    ftbRebate: 4000,
    toronto: true, // Toronto levies an additional municipal LTT (mirrors provincial)
    torontoFtbRebate: 4475,
  },
  BC: {
    name: "British Columbia",
    brackets: [
      { upTo: 200000, rate: 0.01 }, { upTo: 2000000, rate: 0.02 },
      { upTo: 3000000, rate: 0.03 }, { upTo: Infinity, rate: 0.05 },
    ],
    ftbExemptionUpTo: 835000,
  },
  GENERIC: { name: "Other (estimate)", flatRate: 0.01, estimate: true },
};

export const PROVINCE_LIST_SALES = Object.entries(SALES_TAX.provinces).map(
  ([code, v]) => ({ code, name: v.name })
);

// Overlay backend-served live values onto the exported objects (called at app startup).
// Because these are shared object references read at compute-time, mutation propagates everywhere.
export function applyLiveData(live) {
  if (!live) return;
  DATA_SOURCES.length; // no-op ref
  const al = live.account_limits || {};
  if (al.tfsa) Object.assign(ACCOUNT_LIMITS.tfsa, al.tfsa);
  if (al.rrsp) Object.assign(ACCOUNT_LIMITS.rrsp, al.rrsp);
  if (al.fhsa) Object.assign(ACCOUNT_LIMITS.fhsa, al.fhsa);
  if (al.resp) Object.assign(ACCOUNT_LIMITS.resp, al.resp);
  const gb = live.gov_benefits || {};
  if (gb.cpp) Object.assign(GOV_BENEFITS.cpp, gb.cpp);
  if (gb.oas) Object.assign(GOV_BENEFITS.oas, gb.oas);
  const pr = live.payroll || {};
  if (pr.cpp) Object.assign(PAYROLL.cpp, pr.cpp);
  if (pr.ei) Object.assign(PAYROLL.ei, pr.ei);
  if (live.market_rates) Object.assign(MARKET_RATES, live.market_rates);
}
